/**
 * Reset Confirm Dialog Component
 * Feature: 015-custom-prompts
 * Date: 2026-01-04
 *
 * Confirmation modal for resetting custom prompt to system default
 * Prevents accidental resets with clear warning and two-step confirmation
 */

import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X } from 'lucide-react';

interface IResetConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isResetting?: boolean;
}

const ResetConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  isResetting = false,
}: IResetConfirmDialogProps) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-dialog-title"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <h2
                id="reset-dialog-title"
                className="text-lg font-semibold text-gray-900"
              >
                Reset to Default Prompt?
              </h2>
            </div>
            <button
              onClick={onCancel}
              disabled={isResetting}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close dialog"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-gray-700 mb-3">
              Are you sure you want to reset your custom prompt to the system
              default?
            </p>
            <p className="text-sm text-gray-600">
              This action will permanently delete your custom prompt. You can
              always customize it again later.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              onClick={onCancel}
              variant="outline"
              disabled={isResetting}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isResetting}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isResetting ? 'Resetting...' : 'Reset to Default'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export const ResetConfirmDialogMemo = memo(ResetConfirmDialog);
